import { createSlice } from "@reduxjs/toolkit";
import { resolveApiMessage } from "../../helpers/apiMessage";

import {
    createMenuOption,
    deleteMenuOption,
    getMenuOptions,
    updateMenuOption,
} from "./thunk";

export interface MenuOptionsState {
    options: any[];
    loading: boolean;
    loaded: boolean;
    submitting: boolean;
    error: string | null;
}

export const initialState: MenuOptionsState = {
    options: [],
    loading: false,
    loaded: false,
    submitting: false,
    error: null,
};

const extractPayloadData = (payload: any): any => {
    if (!payload) {
        return undefined;
    }

    if (typeof payload === "object" && "data" in payload) {
        return (payload as Record<string, any>).data;
    }

    return payload;
};

const extractResponsePayload = (payload: any): any => {
    if (payload && typeof payload === "object" && "response" in payload) {
        return extractPayloadData((payload as Record<string, any>).response);
    }

    return extractPayloadData(payload);
};

const parseOptionList = (payload: any): any[] => {
    const data = extractResponsePayload(payload);

    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray((data as any)?.items)) {
        return (data as any).items;
    }

    if (Array.isArray((data as any)?.options)) {
        return (data as any).options;
    }

    if (Array.isArray((data as any)?.data)) {
        return (data as any).data;
    }

    return [];
};

const parseOptionItem = (payload: any): any | null => {
    const data = extractResponsePayload(payload);

    if (!data) {
        return null;
    }

    if (Array.isArray(data)) {
        return data[0] ?? null;
    }

    if (data && typeof data === "object") {
        if ((data as any).option && typeof (data as any).option === "object") {
            return (data as any).option;
        }

        if ((data as any).data && typeof (data as any).data === "object" && !Array.isArray((data as any).data)) {
            return (data as any).data;
        }
    }

    return data;
};

const getOptionIdValue = (option: any): string | number | undefined => {
    if (!option || typeof option !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "optionId", "option_id", "uuid", "_id", "slug", "code", "key"];

    for (const key of candidateKeys) {
        const value = (option as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const toComparableId = (value: string | number | undefined | null): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return String(value);
};

const upsertOption = (options: any[], option: any, fallbackId?: string | number) => {
    if (!Array.isArray(options)) {
        return;
    }

    const identifier = getOptionIdValue(option) ?? fallbackId;
    const comparableIdentifier = toComparableId(identifier ?? null);

    if (!comparableIdentifier) {
        options.push(option);
        return;
    }

    const index = options.findIndex(
        (item) => toComparableId(getOptionIdValue(item) ?? null) === comparableIdentifier
    );

    if (index >= 0) {
        options[index] = {
            ...options[index],
            ...option,
        };
        return;
    }

    options.push(
        getOptionIdValue(option) === undefined
            ? {
                  ...option,
                  id: identifier,
              }
            : option
    );
};

const removeOptionById = (options: any[], optionId: string | number): any[] => {
    const comparableIdentifier = toComparableId(optionId);

    if (!comparableIdentifier) {
        return options;
    }

    return options.filter((item) => toComparableId(getOptionIdValue(item) ?? null) !== comparableIdentifier);
};

const getErrorMessage = (payload: any): string => {
    if (!payload) {
        return resolveApiMessage(payload);
    }

    if (typeof payload === "string") {
        return payload;
    }

    if (payload && typeof payload === "object") {
        if (typeof (payload as any).message === "string") {
            return (payload as any).message as string;
        }

        if (typeof (payload as any).error === "string") {
            return (payload as any).error as string;
        }
    }

    return resolveApiMessage(payload);
};

const menuOptionsSlice = createSlice({
    name: "MenuOptions",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getMenuOptions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getMenuOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.loaded = true;
                state.options = parseOptionList(action.payload);
            })
            .addCase(getMenuOptions.rejected, (state, action) => {
                state.loading = false;
                state.loaded = true;
                state.options = [];
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(createMenuOption.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(createMenuOption.fulfilled, (state, action) => {
                state.submitting = false;
                const payload = action.payload as any;
                const option = parseOptionItem(payload?.response ?? payload);
                if (option) {
                    upsertOption(state.options, option);
                } else if (payload?.request) {
                    upsertOption(state.options, payload.request);
                }
            })
            .addCase(createMenuOption.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(updateMenuOption.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(updateMenuOption.fulfilled, (state, action) => {
                state.submitting = false;
                const payload = action.payload as any;
                const optionId = payload?.optionId;
                const option = parseOptionItem(payload?.response ?? payload);

                if (option) {
                    upsertOption(state.options, option, optionId);
                } else if (payload?.request) {
                    upsertOption(state.options, payload.request, optionId);
                }
            })
            .addCase(updateMenuOption.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(deleteMenuOption.fulfilled, (state, action) => {
                const payload = action.payload as any;
                const optionId = payload?.optionId;
                if (optionId !== undefined && optionId !== null) {
                    state.options = removeOptionById(state.options, optionId);
                }
            })
            .addCase(deleteMenuOption.rejected, (state, action) => {
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            });
    },
});

export default menuOptionsSlice.reducer;
